import { denseFromConstraints, type DenseMatrix, dot, infinityNorm, matVec, negate, negateMatrix, transposeMatrix, weightedGram } from "@lpviz/math/blas";
import { trySolveDenseSystem } from "@lpviz/math/lapack";
import type { Constraint } from "@lpviz/math/types";
import { warmStart } from "./limits";
import { NumericTrace, type SolverResult } from "./result";

const SIGMA_MIN = 1e-8;
const SIGMA_MAX = 1 - 1e-8;
const SIGMA_POWER = 3;

export interface IPMOptions {
  /** the primal residual, dual residual and relative gap all have to drop below this */
  tol: number;
  maxit: number;
  alphaMax: number;
  correctorThreshold: number;
  /** Optional primal warm start; need not be feasible (infeasible-start method). */
  startPoint?: readonly number[] | undefined;
}

/**
 * A Mehrotra-style predictor–corrector interior point method on `max c'x s.t. Ax <= b`, written
 * as the minimization of −c over `(−A)x + s = −b, s ≥ 0`. Each iteration solves the normal
 * equations twice (the affine step, then the corrector) and steps a fraction `alphaMax` of the way
 * to the boundary; `mu = s'y / m` is the complementarity measure the 3D path is lifted by.
 */
export function ipm(constraints: readonly Constraint[], objective: Float64Array, opts: IPMOptions): SolverResult {
  const { tol, maxit, alphaMax, correctorThreshold, startPoint } = opts;

  // Ax <= b becomes (-A)x + s = -b with slack s >= 0, and max becomes min.
  const { A: aOriginal, b: bOriginal } = denseFromConstraints(constraints);
  const A = negateMatrix(aOriginal);
  const At = transposeMatrix(A);
  const b = negate(bOriginal);
  const c = negate(objective);
  const m = A.rows;
  const n = A.cols;

  const trace = new NumericTrace(n, "µ", maxit);

  const x = new Float64Array(n);
  const s = new Float64Array(m).fill(1);
  const y = new Float64Array(m).fill(1);
  const x0 = warmStart(startPoint, n);
  if (x0) {
    // Relocate only the primal point and keep s = y = 1, so a start at the default position
    // reproduces the cold trajectory. x0 need not be feasible (infeasible-start method), but
    // (s, y) must stay strictly positive: the fraction-to-boundary rule only preserves the
    // positivity it starts with, and s_i*y_i = 1 keeps the initial mu on scale for sigma.
    x.set(x0);
  }

  const ax = new Float64Array(m);
  const aty = new Float64Array(n);
  const rP = new Float64Array(m);
  const rD = new Float64Array(n);
  const rC = new Float64Array(m);
  const zeroP = new Float64Array(m);
  const zeroD = new Float64Array(n);
  const normal = new NormalEquations(A, At, s, y);
  const dxAff = new Float64Array(n);
  const dsAff = new Float64Array(m);
  const dyAff = new Float64Array(m);
  const dxCor = new Float64Array(n);
  const dsCor = new Float64Array(m);
  const dyCor = new Float64Array(m);
  const dx = new Float64Array(n);
  const ds = new Float64Array(m);
  const dy = new Float64Array(m);

  let converged = false;
  let failureMessage: string | null = null;
  // null once the normal equations are solved, else why they could not be
  const solve = (label: string, rP: Float64Array, rD: Float64Array, rC: Float64Array, dx: Float64Array, ds: Float64Array, dy: Float64Array): string | null =>
    normal.solve(rP, rD, rC, dx, ds, dy) ? null : `IPM ${label} solve failed: Singular linear system`;

  while (trace.count < maxit) {
    matVec(A, x, ax);
    matVec(At, y, aty);

    for (let i = 0; i < m; i++) {
      rP[i] = b[i]! - ax[i]! + s[i]!;
    }
    for (let j = 0; j < n; j++) {
      rD[j] = c[j]! - aty[j]!;
    }

    const mu = dot(s, y) / m;
    const pObj = dot(c, x);
    const gap = Math.abs(pObj - dot(b, y)) / (1 + Math.abs(pObj));
    const pRes = infinityNorm(rP);

    trace.record(x, -pObj, pRes, mu);

    if (pRes <= tol && infinityNorm(rD) <= tol && gap <= tol) {
      converged = true;
      break;
    }

    normal.form();
    for (let i = 0; i < m; i++) rC[i] = -s[i]! * y[i]!;
    failureMessage = solve("linear", rP, rD, rC, dxAff, dsAff, dyAff);
    if (failureMessage) break;

    const alphaP = fractionToBoundary(s, dsAff);
    const alphaD = fractionToBoundary(y, dyAff);
    let muAff = 0;
    for (let i = 0; i < m; i++) {
      muAff += (s[i]! + alphaP * dsAff[i]!) * (y[i]! + alphaD * dyAff[i]!);
    }
    muAff /= m;

    if (!(alphaP >= correctorThreshold && alphaD >= correctorThreshold)) {
      // mu can reach exactly 0 when alphaMax = 1; (0/0)**p would be NaN
      const sigma = mu > 0 ? Math.max(SIGMA_MIN, Math.min(SIGMA_MAX, (muAff / mu) ** SIGMA_POWER)) : SIGMA_MIN;
      for (let i = 0; i < m; i++) {
        rC[i] = -(dsAff[i]! * dyAff[i]! - sigma * mu);
      }
      failureMessage = solve("corrector", zeroP, zeroD, rC, dxCor, dsCor, dyCor);
      if (failureMessage) break;

      for (let j = 0; j < n; j++) dx[j] = dxAff[j]! + dxCor[j]!;
      for (let i = 0; i < m; i++) {
        ds[i] = dsAff[i]! + dsCor[i]!;
        dy[i] = dyAff[i]! + dyCor[i]!;
      }
    } else {
      dx.set(dxAff);
      ds.set(dsAff);
      dy.set(dyAff);
    }

    const stepP = alphaMax * fractionToBoundary(s, ds);
    const stepD = alphaMax * fractionToBoundary(y, dy);
    for (let j = 0; j < n; j++) x[j]! += dx[j]! * stepP;
    for (let i = 0; i < m; i++) {
      s[i]! += ds[i]! * stepP;
      y[i]! += dy[i]! * stepD;
    }
  }

  return trace.finish(failureMessage ? { converged: false, lead: "Stopped", before: failureMessage } : { converged });
}

//   [ A  -I   0 ] [dx]   [rP]        (primal residual)
//   [ 0   0  Aᵀ ] [ds] = [rD]        (dual residual)
//   [ 0   Y   S ] [dy]   [rC]        (complementarity)
//
//   (Aᵀ D A) dx = Aᵀ((rC + y∘rP)/s) − rD,   D = diag(y/s),
class NormalEquations {
  private readonly M: Float64Array;
  private readonly luScratch: Float64Array;
  private readonly rhs: Float64Array;
  private readonly weighted: Float64Array;

  constructor(
    private readonly A: DenseMatrix,
    private readonly At: DenseMatrix,
    private readonly s: Float64Array,
    private readonly y: Float64Array,
  ) {
    const n = A.cols;
    this.M = new Float64Array(n * n);
    this.luScratch = new Float64Array(n * n);
    this.rhs = new Float64Array(n);
    this.weighted = new Float64Array(A.rows);
  }

  /** Rebuild Aᵀ D A for the current s and y. */
  form(): void {
    const { A, s, y, M } = this;
    weightedGram(A, (i) => y[i]! / s[i]!, M);
  }

  /** The step into dx, ds and dy; false when the normal matrix is singular. */
  solve(rP: Float64Array, rD: Float64Array, rC: Float64Array, dx: Float64Array, ds: Float64Array, dy: Float64Array): boolean {
    const { A, At, s, y, M, rhs, weighted, luScratch } = this;
    const { rows: m, cols: n } = A;
    for (let i = 0; i < m; i++) {
      weighted[i] = (rC[i]! + y[i]! * rP[i]!) / s[i]!;
    }
    matVec(At, weighted, rhs);
    for (let j = 0; j < n; j++) rhs[j]! -= rD[j]!;
    if (!trySolveDenseSystem(M, n, rhs, dx, luScratch)) return false;
    matVec(A, dx, ds);
    for (let i = 0; i < m; i++) {
      ds[i]! -= rP[i]!;
      dy[i] = (rC[i]! - y[i]! * ds[i]!) / s[i]!;
    }
    return true;
  }
}

// The largest step in [0, 1] that keeps every `values[i] + alpha * delta[i]` nonnegative.
function fractionToBoundary(values: Float64Array, delta: Float64Array) {
  let alpha = 1;
  for (let i = 0; i < values.length; i++) {
    const direction = delta[i]!;
    if (direction < 0) {
      alpha = Math.min(alpha, -values[i]! / direction);
    }
  }
  return alpha;
}
